import mitt from 'mitt';
import { DropzoneFile } from '@lendica/components';

export type ProcessingStatus = 'pending' | 'processing' | 'completed' | 'error';

export interface InvoiceProcessingState {
    fileKey: string;
    fileName: string;
    status: ProcessingStatus;
    invoiceId?: string;
    error?: string;
}

type InvoiceEvents = {
    'processing-started': { fileKey: string };
    'processing-complete': { fileKey: string; invoiceId: string };
    'processing-error': { fileKey: string; error: string };
    'state-update': void;
};

type ProcessInvoiceResponse = { invoice: { id: string } };

class InvoiceUploadManagerClass {
    private emitter = mitt<InvoiceEvents>();
    private processingState = new Map<string, InvoiceProcessingState>();
    private processingQueue: string[] = [];
    private activeProcessing = 0;
    private readonly MAX_CONCURRENT = 3;
    private processInvoice?: (objectKey: string) => Promise<ProcessInvoiceResponse>;

    constructor() {
        console.log('[InvoiceUploadManager] Initialized');
    }

    setProcessInvoice(processor: (objectKey: string) => Promise<ProcessInvoiceResponse>): void {
        this.processInvoice = processor;
    }

    /**
     * Start processing a file
     */
    async startProcessing(file: DropzoneFile): Promise<void> {
        if (!file.key) {
            console.error('[InvoiceUploadManager] Cannot process file without key');
            return;
        }

        // Check if already processing
        if (this.processingState.has(file.key)) {
            console.log('[InvoiceUploadManager] File already being processed:', file.key);
            return;
        }

        // Initialize state
        this.processingState.set(file.key, {
            fileKey: file.key,
            fileName: file.path || file.name || 'unknown',
            status: 'pending',
        });

        // Add to queue
        this.processingQueue.push(file.key);
        this.emitter.emit('state-update');

        // Process next in queue
        this.processNextInQueue();
    }

    private async processNextInQueue(): Promise<void> {
        if (this.activeProcessing >= this.MAX_CONCURRENT || this.processingQueue.length === 0) {
            return;
        }

        const fileKey = this.processingQueue.shift()!;
        const state = this.processingState.get(fileKey);
        if (!state) return;

        // Update status
        state.status = 'processing';
        this.activeProcessing++;
        this.emitter.emit('processing-started', { fileKey });
        this.emitter.emit('state-update');

        try {
            console.log('[InvoiceUploadManager] Processing invoice:', fileKey);
            if (!this.processInvoice) {
                throw new Error('No processInvoice function configured');
            }
            const result = await this.processInvoice(fileKey);

            // Update state
            state.status = 'completed';
            state.invoiceId = result.invoice.id;

            this.emitter.emit('processing-complete', { fileKey, invoiceId: result.invoice.id });
            console.log('[InvoiceUploadManager] Processing complete:', fileKey, result.invoice.id);
        } catch (error: any) {
            console.error('[InvoiceUploadManager] Processing failed:', fileKey, error);

            // Update state
            state.status = 'error';
            state.error = error.message || 'Processing failed';

            this.emitter.emit('processing-error', {
                fileKey,
                error: state.error || 'Processing failed',
            });
        } finally {
            this.activeProcessing--;
            this.emitter.emit('state-update');

            // Process next in queue
            this.processNextInQueue();
        }
    }

    /**
     * Get processing state for a file
     */
    getState(fileKey: string): InvoiceProcessingState | undefined {
        return this.processingState.get(fileKey);
    }

    /**
     * Get all processing states
     */
    getAllStates(): Map<string, InvoiceProcessingState> {
        return new Map(this.processingState);
    }

    /**
     * Clear completed or errored states
     */
    clearCompleted(): void {
        const toRemove: string[] = [];
        this.processingState.forEach((state, key) => {
            if (state.status === 'completed' || state.status === 'error') {
                toRemove.push(key);
            }
        });
        toRemove.forEach(key => this.processingState.delete(key));
        this.emitter.emit('state-update');
    }

    /**
     * Subscribe to events
     */
    on<K extends keyof InvoiceEvents>(
        event: K,
        handler: (payload: InvoiceEvents[K]) => void
    ): void {
        this.emitter.on(event, handler);
    }

    /**
     * Unsubscribe from events
     */
    off<K extends keyof InvoiceEvents>(
        event: K,
        handler: (payload: InvoiceEvents[K]) => void
    ): void {
        this.emitter.off(event, handler);
    }
}

// Singleton instance
export const InvoiceUploadManager = new InvoiceUploadManagerClass();
